import { useQuery } from '@apollo/client/react'
import {
  Avatar,
  Badge,
  EmptyState,
  ErrorState,
  LoadingState,
  PageLayout,
  Section,
  linkClasses,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { Link, useParams } from '@tanstack/react-router'

import { usePageTitle } from '../app/page-title.js'
import { SubjectProgressSection } from '../features/progress/SubjectProgressSection.js'
import { PracticeHistoryGroup } from '../features/students/PracticeHistoryGroup.js'
import { StudentDetailDocument } from '../graphql/generated/operations.js'

/**
 * One student's work, grouped by practice.
 *
 * Every tutor linked to a student sees this, not only the one who assigned the
 * practice: a shared student is the case the design draws, and section 7 makes
 * attempt results visible to all of them deliberately.
 *
 * Grouped rather than listed, because "how is she doing at fractions" is a
 * question about a practice over time and a flat list answers a different one.
 */
export function StudentScreen() {
  const t = useTranslate()
  const language = useLanguage()
  const { studentId } = useParams({ from: '/$language/tutor/students/$studentId' })
  const { data, loading, error } = useQuery(StudentDetailDocument, { variables: { studentId } })

  usePageTitle(data?.studentHistory.student.displayName ?? t('student.title'))

  if (loading) return <LoadingState>{t('common.loading')}</LoadingState>
  if (error) return <ErrorState>{t('student.loadError')}</ErrorState>
  if (!data) return null

  const { student, practices, subjects } = data.studentHistory

  return (
    <PageLayout width="wide">
      <Link
        to="/$language/students"
        params={{ language }}
        className={`${linkClasses({ size: 'sm', layout: 'standalone' })} self-start`}
      >
        {t('student.backToStudents')}
      </Link>

      <div className="grid min-w-0 gap-8 desktop:grid-cols-[minmax(15rem,18rem)_minmax(0,1fr)] desktop:items-start">
        {/* Compact context rail. The shell's h1 already names the student, so
            this repeats the identity as profile content rather than another
            heading. A divider, not a card, separates it from the work. */}
        <aside
          className="flex min-w-0 flex-col gap-4 border-b border-border pb-6 desktop:border-r desktop:border-b-0 desktop:pr-6 desktop:pb-0"
          aria-label={student.displayName}
        >
          <div className="flex min-w-0 items-center gap-3 desktop:flex-col desktop:items-start">
            <Avatar name={student.displayName} size="lg" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="break-words text-h4 font-semibold text-text">{student.displayName}</p>
              {/* Age is the server's answer, worked out from the birth date
                  on this request. Nothing here stores or recomputes it. */}
              <p className="text-body-sm text-text-secondary">
                {student.age === null ? t('student.noBirthDate') : t('student.age', [student.age])}
              </p>
              <p className="break-all text-body-sm text-text-secondary">
                {student.email ?? t('student.noEmail')}
              </p>
            </div>
          </div>
          <Badge status={student.claimed ? 'published' : 'draft'}>
            {student.claimed ? t('student.signedIn') : t('student.noAccountYet')}
          </Badge>
        </aside>

        {/* The chart component owns the only analytical boundary. Its heading,
            filters and summary remain on this wider canvas. */}
        <div className="flex min-w-0 flex-col gap-8">
          <SubjectProgressSection studentName={student.displayName} series={subjects} />

          <Section id="by-practice" title={t('student.byPractice')}>
            {practices.length === 0 ? (
              <EmptyState
                variant="section"
                title={t('student.noPracticeFinishedTitle', [student.displayName])}
                description={t('student.noPracticeFinishedDescription')}
              />
            ) : (
              <div className="flex flex-col gap-4">
                {practices.map((group) => (
                  <PracticeHistoryGroup
                    key={group.practiceId}
                    title={group.title}
                    attemptCount={group.attemptCount}
                    averagePercent={group.averagePercent}
                    bestPercent={group.bestPercent}
                    attempts={group.attempts}
                  />
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </PageLayout>
  )
}
